// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { fetchGet, fetchPost } from '@/api/http';
import type { DurableRunSummaryInput } from '@/lib/projector/runSummary';
import {
  createControlOperation,
  submitControlOperation,
} from './controlOperations';

export type ProjectRunsResponse = {
  project_id?: unknown;
  runs?: Array<{
    run_id?: unknown;
    status?: unknown;
    total_attempt_elapsed_ms?: unknown;
    created_at?: unknown;
    updated_at?: unknown;
    resolved_model?: unknown;
    [key: string]: unknown;
  }>;
  has_more?: unknown;
  cloud_restore_pending?: unknown;
};

/** The non-secret model identity a Run's latest bound attempt ran on. */
export interface ResolvedRunModel {
  platform: string;
  type: string;
}

/** Anything but a complete identity is unknown, never a guessed model. */
export function parseResolvedRunModel(value: unknown): ResolvedRunModel | null {
  if (!value || typeof value !== 'object') return null;
  const { platform, type } = value as Record<string, unknown>;
  if (typeof platform !== 'string' || !platform.trim()) return null;
  if (typeof type !== 'string' || !type.trim()) return null;
  return { platform, type };
}

/** Owner-checked `run_id → model` entries from one Run-list response. */
export function projectResolvedRunModels(
  projectId: string,
  response: ProjectRunsResponse
): Record<string, ResolvedRunModel | null> {
  if (response?.project_id !== projectId || !Array.isArray(response.runs))
    return {};
  const models: Record<string, ResolvedRunModel | null> = {};
  for (const run of response.runs) {
    if (run?.project_id !== projectId) continue;
    if (typeof run.run_id !== 'string' || !run.run_id) continue;
    models[run.run_id] = parseResolvedRunModel(run.resolved_model);
  }
  return models;
}

const EMPTY_RESOLVED_RUN_MODELS: Readonly<
  Record<string, ResolvedRunModel | null>
> = Object.freeze({});
const resolvedRunModelsByProject = new Map<
  string,
  Readonly<Record<string, ResolvedRunModel | null>>
>();
const resolvedRunModelListeners = new Map<string, Set<() => void>>();

/**
 * Every Run-list read already carries each Run's model, so the Session's
 * model map is filled from those reads instead of a fetch of its own. A
 * bounded read (for example the newest Run only) updates just its rows.
 */
export function recordResolvedRunModels(
  projectId: string,
  response: ProjectRunsResponse
): void {
  const incoming = projectResolvedRunModels(projectId, response);
  const existing =
    resolvedRunModelsByProject.get(projectId) ?? EMPTY_RESOLVED_RUN_MODELS;
  const changed = Object.entries(incoming).some(
    ([runId, model]) =>
      !(runId in existing) ||
      existing[runId]?.platform !== model?.platform ||
      existing[runId]?.type !== model?.type
  );
  if (!changed) return;
  resolvedRunModelsByProject.set(projectId, { ...existing, ...incoming });
  resolvedRunModelListeners.get(projectId)?.forEach((listener) => listener());
}

export function getResolvedRunModels(
  projectId: string
): Readonly<Record<string, ResolvedRunModel | null>> {
  return resolvedRunModelsByProject.get(projectId) ?? EMPTY_RESOLVED_RUN_MODELS;
}

export function subscribeResolvedRunModels(
  projectId: string,
  listener: () => void
): () => void {
  let listeners = resolvedRunModelListeners.get(projectId);
  if (!listeners) {
    listeners = new Set();
    resolvedRunModelListeners.set(projectId, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners!.delete(listener);
    if (listeners!.size === 0) resolvedRunModelListeners.delete(projectId);
  };
}

/** Test seam: forget every recorded model. */
export function resetResolvedRunModelsForTest(): void {
  resolvedRunModelsByProject.clear();
}

/** Only owner-checked rows may enter the canonical projection. */
export function projectRunSummaries(
  projectId: string,
  response: ProjectRunsResponse
): DurableRunSummaryInput[] {
  if (response.project_id !== projectId) return [];
  return (response.runs ?? []).filter(
    (run) =>
      run.project_id === projectId &&
      typeof run.run_id === 'string' &&
      typeof run.status === 'string' &&
      (typeof run.updated_at === 'number' || typeof run.updated_at === 'string')
  ) as DurableRunSummaryInput[];
}

type RunControlRequest = (
  url: string,
  data: { request_id: string; reason: string }
) => Promise<unknown>;

type InFlightProjectRunsRequest = {
  controller: AbortController;
  request: Promise<ProjectRunsResponse>;
  settled: boolean;
  subscribers: number;
};

const inFlightProjectRuns = new Map<string, InFlightProjectRunsRequest>();

export const ACTIVE_DURABLE_RUN_STATUSES = [
  'pending',
  'running',
  'waiting_for_user',
] as const;

function abortError(signal: AbortSignal): Error {
  if (signal.reason instanceof Error) return signal.reason;
  // DOMException can come from a different browser/jsdom realm and therefore
  // fail `instanceof Error`. Preserve its semantic name so a hydration
  // deadline remains retryable instead of looking like navigation cleanup.
  if (
    signal.reason &&
    typeof signal.reason === 'object' &&
    'name' in signal.reason &&
    'message' in signal.reason
  ) {
    const error = new Error(String(signal.reason.message));
    error.name = String(signal.reason.name);
    return error;
  }
  return new DOMException('Project Run loading was aborted', 'AbortError');
}

function waitForCaller<T>(
  request: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(abortError(signal));

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(abortError(signal));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    request.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

/**
 * Share concurrent canonical Run-list reads for one Project.
 *
 * The legacy Project projection and the event-native projection hydrate at the
 * same time while the migration flag is enabled. They need the same payload,
 * so issuing two identical SQLite reads only adds work and makes first paint
 * wait behind duplicate traffic. Each caller owns only its subscription. The
 * shared HTTP request is aborted once every subscriber has left, which keeps a
 * previous Space from occupying Brain's RunJournal read lane after navigation.
 */
export function fetchProjectRuns(
  projectId: string,
  limit = 100,
  signal?: AbortSignal,
  expectedAccountKey?: string
): Promise<ProjectRunsResponse> {
  const key = `${expectedAccountKey ?? ''}\u0000${projectId}\u0000${limit}`;
  let entry = inFlightProjectRuns.get(key);
  if (!entry) {
    const controller = new AbortController();
    const request = Promise.resolve(
      fetchGet('/runs', { project_id: projectId, limit }, undefined, {
        signal: controller.signal,
        ...(expectedAccountKey ? { expectedAccountKey } : {}),
      })
    ) as Promise<ProjectRunsResponse>;
    entry = {
      controller,
      request,
      settled: false,
      subscribers: 0,
    };
    inFlightProjectRuns.set(key, entry);
    void request.then(
      (response) => {
        recordResolvedRunModels(projectId, response);
        entry!.settled = true;
        if (inFlightProjectRuns.get(key) === entry) {
          inFlightProjectRuns.delete(key);
        }
      },
      () => {
        entry!.settled = true;
        if (inFlightProjectRuns.get(key) === entry) {
          inFlightProjectRuns.delete(key);
        }
      }
    );
  }

  const subscribedEntry = entry;
  subscribedEntry.subscribers += 1;
  return waitForCaller(subscribedEntry.request, signal).finally(() => {
    subscribedEntry.subscribers = Math.max(0, subscribedEntry.subscribers - 1);
    if (subscribedEntry.settled || subscribedEntry.subscribers > 0) return;
    if (inFlightProjectRuns.get(key) === subscribedEntry) {
      inFlightProjectRuns.delete(key);
    }
    subscribedEntry.controller.abort();
  });
}

/** Read only canonical Runs that still own live execution state. */
export function fetchActiveProjectRuns(
  projectId: string,
  signal?: AbortSignal,
  limit = 1
): Promise<ProjectRunsResponse> {
  return fetchGet(
    '/runs',
    {
      project_id: projectId,
      status: ACTIVE_DURABLE_RUN_STATUSES,
      limit,
    },
    undefined,
    { signal }
  ) as Promise<ProjectRunsResponse>;
}

/** Cancel one exact durable Run; callers own stable request-id generation. */
export function cancelProjectRun(
  runId: string,
  requestId: string,
  reason: string,
  request: RunControlRequest = fetchPost,
  projectId?: string
): Promise<unknown> {
  if (request !== fetchPost)
    return request(`/runs/${encodeURIComponent(runId)}/cancel`, {
      request_id: requestId,
      reason,
    });
  return submitControlOperation(
    createControlOperation({
      kind: 'cancel',
      runId,
      projectId,
      path: `/runs/${encodeURIComponent(runId)}/cancel`,
      body: { request_id: requestId, reason },
    })
  );
}
